import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TOUR_STOPS = [
  {
    path: '/offers',
    title: 'Offers',
    description: "This is where all discovered opportunities live. Use the score filter to see the best ones and the vertical filter to narrow by category.",
  },
  {
    path: '/pipeline',
    title: 'Pipeline',
    description: "Track the progress of offers you're working on. Offers move through stages from Assigned to In Market.",
  },
  {
    path: '/creative',
    title: 'Creative Hub',
    description: "Start and manage your creative sessions here. Click into any session to pick up where you left off.",
  },
  {
    path: '/creative',
    title: 'Creative Engine',
    description: "This is where you build creatives. The AI generates psychology maps, angles, hooks, and scripts. Use the chat panel to guide the AI with your own ideas.",
  },
  {
    path: '/campaign-health',
    title: 'Campaign Health',
    description: "Monitor your active campaigns. The system flags At Risk campaigns and Scale Candidates automatically.",
  },
  {
    path: null,
    title: 'Telegram',
    description: "You'll receive alerts and can send commands to the bot in the Robot Squad group. Type natural language requests like 'find me fitness gadgets under $30'.",
  },
];

export const OnboardingTour = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStop, setCurrentStop] = useState(0);
  const [visible, setVisible] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!user || user.role === 'FOUNDER') { setChecked(true); return; }

    const check = async () => {
      try {
        const res = await axios.get(`${API}/operators/me/onboarding`);
        if (!res.data.completed && !res.data.is_founder) {
          setVisible(true);
        }
      } catch {}
      setChecked(true);
    };
    check();
  }, [user]);

  const handleNext = () => {
    if (currentStop < TOUR_STOPS.length - 1) {
      const nextStop = currentStop + 1;
      setCurrentStop(nextStop);
      const nextPath = TOUR_STOPS[nextStop].path;
      if (nextPath && location.pathname !== nextPath) {
        navigate(nextPath);
      }
    } else {
      handleComplete();
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = async () => {
    setVisible(false);
    try {
      await axios.post(`${API}/operators/me/onboarding/complete`);
    } catch {}
  };

  if (!checked || !visible) return null;

  const stop = TOUR_STOPS[currentStop];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="onboarding-overlay">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl" data-testid="onboarding-modal">
        {/* Progress */}
        <div className="flex items-center gap-1 mb-6">
          {TOUR_STOPS.map((_, i) => (
            <div
              key={i}
              className={`flex-1 h-1 rounded-full transition-colors ${
                i <= currentStop ? 'bg-emerald-500' : 'bg-slate-700'
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs text-emerald-400 font-medium">Step {currentStop + 1} of {TOUR_STOPS.length}</span>
          </div>
          <h3 className="text-xl font-bold text-white mb-3" data-testid="tour-stop-title">{stop.title}</h3>
          <p className="text-slate-300 text-sm leading-relaxed" data-testid="tour-stop-description">{stop.description}</p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            onClick={handleSkip}
            className="text-sm text-slate-500 hover:text-white transition-colors"
            data-testid="skip-tour-btn"
          >
            Skip Tour
          </button>
          <button
            onClick={handleNext}
            className="px-6 py-2.5 bg-emerald-600 text-white rounded-lg font-medium text-sm hover:bg-emerald-500 transition-colors"
            data-testid="next-tour-btn"
          >
            {currentStop === TOUR_STOPS.length - 1 ? "Got it!" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
